import { darken, lighten } from "polished";
import React from "react";
import styled, { ThemeProvider, css } from "styled-components";
import theme from "../../style/theme";
import ButtonBase from "./ButtonBase";

/* 타입 선언 ButtonProps */
interface ButtonProps {
  /**
   * children: 버튼 속 text
   */
  children?: string;
  /**
   * name: dom 요소 접근
   */
  name?: string;
  /**
   * color: 버튼 color
   */
  color?: "primary" | "secondary" | "tertiary" | "quaternary" | "iconOnly";
  /**
   * disable: 버튼 활성화
   */
  disable?: boolean;
  /**
   * size: 버튼 size
   */
  size?:
    | "rect"
    | "standard"
    | "medium"
    | "pillMedium"
    | "pillSmall"
    | "square"
    | "circle";
  /**
   * sx: 버튼 커스텀 css
   */
  sx?: any; // SxProps<Theme>
  /**
   * 버튼 앞 아이콘: 버튼 아이콘
   */
  startIcon?: React.ReactNode;
  /**
   * 버튼 뒤 아이콘: 버튼 아이콘
   */
  endIcon?: React.ReactNode;
  /**
   * onClick 이벤트
   */
  onClick?: (e: any) => void;
  /**
   * class
   */
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

/* 컴포넌트 Button */
const Button: React.FC<ButtonProps> = ({
  children,
  name,
  startIcon,
  endIcon,
  onClick,
  className,
  ref,
  ...rest
}) => {
  return (
    <ThemeProvider theme={theme}>
      <ButtonRoot
        ref={ref}
        name={name}
        onClick={onClick}
        className={className}
        {...rest}
      >
        {startIcon && <StartIconRoot>{startIcon}</StartIconRoot>}
        {children}
        {endIcon && <EndIconRoot>{endIcon}</EndIconRoot>}
      </ButtonRoot>
    </ThemeProvider>
  );
};

export default Button;

/* color styles */
const colorStyles: any = css`
  /* color primary */
  ${(props: any) => {
    const selected = props.theme.palette.primitives.yellow400;
    return css`
      ${(props: any) =>
        props.color === "primary" &&
        css`
          color: ${(props: any) => props.theme.palette.primitives.black};
          background: ${selected};
          &:hover {
            background: ${lighten(0.005, `${selected}`)};
          }
          &:active {
            background: ${darken(0.1, `${selected}`)};
          }
        `}
    `;
  }};
  /* color secondary */
  ${(props: any) => {
    const selected = props.theme.palette.primitives.yellow100;
    return css`
      ${(props: any) =>
        props.color === "secondary" &&
        css`
          color: ${(props: any) => props.theme.palette.primitives.black};
          background: ${selected};
          border: 1px solid
            ${(props: any) => props.theme.palette.primitives.yellow400};
          &:hover {
            background: ${lighten(0.005, `${selected}`)};
          }
          &:active {
            background: ${darken(0.1, `${selected}`)};
          }
        `}
    `;
  }};
  /* color tertiary */
  ${(props: any) => {
    const selected = props.theme.palette.primitives.gray50;
    return css`
      ${(props: any) =>
        props.color === "tertiary" &&
        css`
          color: ${(props: any) => props.theme.palette.primitives.gray500};
          background: ${selected};
          &:hover {
            background: ${lighten(0.005, `${selected}`)};
          }
          &:active {
            background: ${darken(0.1, `${selected}`)};
          }
        `}
    `;
  }};
  /* color quaternary */
  ${(props: any) => {
    const selected = "transparent";
    const backgroundColor = props.theme.palette.primitives.gray50;
    return css`
      ${(props: any) =>
        props.color === "quaternary" &&
        css`
          color: ${(props: any) => props.theme.palette.primitives.gray500};
          background: ${selected};
          &:hover {
            background: ${lighten(0.00005, `${backgroundColor}`)};
          }
          &:active {
            background: ${darken(0.02, `${backgroundColor}`)};
          }
        `}
    `;
  }};
  /* color iconOnly */
  ${(props: any) => {
    const selected = "transparent";
    return css`
      ${(props: any) =>
        props.color === "iconOnly" &&
        css`
          background: ${selected};
          &:active {
            & path {
              fill: ${(props: any) => props.theme.palette.primitives.gray500};
            }
          }
        `}
    `;
  }};
`;

/* size styles */
const sizeStyles: any = css`
  ${(props: any) =>
    props.size === "rect" &&
    css`
      min-width: 360px;
      height: 88px;
      font-size: 28px;
      font-weight: 600;
      border: 0;
      border-radius: 0;
      & span:first-child {
        padding: 0;
        padding-right: 16px;
      }
    `}
  ${(props: any) =>
    props.size === "standard" &&
    css`
      min-width: 328px;
      height: 60px;
      font-size: 28px;
      font-weight: 600;
      border-radius: 32px;
      & span:first-child {
        padding: 0;
        padding-right: 16px;
      }
    `}
  ${(props: any) =>
    props.size === "pillMedium" &&
    css`
      min-width: 78px;
      padding: 14px 16px;
      height: 56px;
      font-size: 18px;
      line-height: 28px;
      font-weight: 600;
      border-radius: 999px;
      & span {
        padding: 0;
        padding-right: 8px;
      }
    `}
    ${(props: any) =>
    props.size === "pillSmall" &&
    css`
      padding: 10px 12px;
      min-width: 41px;
      height: 44px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      border-radius: 36px;
      & span {
        padding: 0;
        padding-right: 8px;
      }
    `}
    ${(props: any) =>
    props.size === "square" &&
    css`
      width: 56px;
      height: 56px;
      padding: 0 16px 0 16px;
    `}
    ${(props: any) =>
    props.size === "circle" &&
    css`
      width: 56px;
      height: 56px;
      border-radius: 56px;
    `}
`;

/* disable styles */
const disableStyles: any = css`
  ${(props: any) =>
    props.disable === true &&
    css`
      color: ${(props: any) => props.theme.palette.primitives.gray300};
      background: ${(props: any) => props.theme.palette.primitives.gray50};
      border: 1px solid transparent;
      &:hover {
        background: ${(props: any) => props.theme.palette.primitives.gray50};
      }
      &:active {
        background: ${(props: any) => props.theme.palette.primitives.gray50};
      }
      & svg path {
        fill: ${(props: any) => props.theme.palette.primitives.gray300};
      }
    `}

  ${(props: any) =>
    props.disable === true &&
    props.color === "quaternary" &&
    css`
      color: ${(props: any) => props.theme.palette.primitives.gray300};
      background: transparent;
      border: 1px solid transparent;
      &:hover {
        background: ${(props: any) => props.theme.palette.primitives.gray50};
      }
      &:active {
        background: ${(props: any) => props.theme.palette.primitives.gray50};
      }
      & svg path {
        fill: ${(props: any) => props.theme.palette.primitives.gray300};
      }
    `}
`;

/* icon style 컴포넌트 */
const StartIconRoot: any = styled.span`
  display: flex;
  align-items: center;
  // padding: 0 16px;
`;

const EndIconRoot: any = styled.span`
  display: flex;
  align-items: center;
  padding: 0 16px;

  ${(props: any) =>
    props.children &&
    css`
      position: absolute;
      right: 0;
    `};
`;

/* 버튼 스타일 컴포넌트 */
const ButtonRoot = styled(ButtonBase).attrs((props: any) => ({
  startIcon: `${(props: any) => props.startIcon}`,
  endIcon: `${(props: any) => props.endIcon}`,
}))`
  /* basic styles */
  border: 1px solid transparent;
  /* color styles */
  ${colorStyles};
  /* size styles */
  ${sizeStyles};

  /* css 커스텀 */
  ${(props: ButtonProps) => props.sx};
  /* disable styles */
  ${disableStyles};
`;

/* 기본 값 */
Button.defaultProps = {
  color: "primary",
  size: "standard",
  disable: false,
};
